/* =====================================================================
   XMarket · Home Animations Orchestrator — 2026-04-29
   Mezcla GSAP (donde aporta) + IntersectionObserver (más confiable).
   Respeta prefers-reduced-motion. Degrada a HTML estático sin JS.
   ===================================================================== */
(function () {
  'use strict';

  const reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.documentElement.classList.add('xm-js');

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(() => {
    if (reduceMotion) {
      document.querySelectorAll('[data-xm-reveal], [data-xm-reveal-group]').forEach((el) => {
        el.classList.add('xm-revealed');
      });
      return;
    }

    /* ---------- 1. Reveal con IntersectionObserver (no depende de GSAP) ---------- */
    const revealObserver = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('xm-revealed');
            // Stagger manual con CSS variable para los hijos de un group
            if (entry.target.matches('[data-xm-reveal-group]')) {
              const stagger = parseFloat(entry.target.dataset.xmStagger || '0.08');
              [...entry.target.children].forEach((child, i) => {
                child.style.transitionDelay = `${i * stagger}s`;
              });
            }
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -8% 0px' }
    );
    document
      .querySelectorAll('[data-xm-reveal], [data-xm-reveal-group]')
      .forEach((el) => revealObserver.observe(el));

    // Fallback de seguridad: si pasados 1.5s un elemento sigue sin "revealed",
    // se revela igual (evita pantallas en blanco si IO falla).
    setTimeout(() => {
      document
        .querySelectorAll('[data-xm-reveal]:not(.xm-revealed), [data-xm-reveal-group]:not(.xm-revealed)')
        .forEach((el) => el.classList.add('xm-revealed'));
    }, 1500);

    /* ---------- 2. Scroll progress bar fija arriba ---------- */
    const bar = document.createElement('div');
    bar.className = 'xm-scroll-progress';
    document.body.appendChild(bar);
    const updateBar = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = `scaleX(${h > 0 ? window.scrollY / h : 0})`;
    };
    window.addEventListener('scroll', updateBar, { passive: true });
    updateBar();

    /* ---------- 3. Magnetic CTA (cursor pull) ---------- */
    document.querySelectorAll('[data-xm-magnetic], .xm-hc__cta--primary').forEach((el) => {
      el.addEventListener('mousemove', (e) => {
        const r = el.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        el.style.transform = `translate(${x * 0.18}px, ${y * 0.22}px)`;
      });
      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
      });
    });

    /* ---------- 4. Drag-to-scroll para category rail ---------- */
    document.querySelectorAll('[data-xm-drag]').forEach((track) => {
      let isDown = false, startX = 0, startScroll = 0;
      track.addEventListener('mousedown', (e) => {
        isDown = true;
        startX = e.pageX - track.offsetLeft;
        startScroll = track.scrollLeft;
        track.classList.add('xm-dragging');
      });
      const stop = () => { isDown = false; track.classList.remove('xm-dragging'); };
      track.addEventListener('mouseleave', stop);
      track.addEventListener('mouseup', stop);
      track.addEventListener('mousemove', (e) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - track.offsetLeft;
        track.scrollLeft = startScroll - (x - startX) * 1.4;
      });
    });

    /* ---------- 5. Countdown ---------- */
    document.querySelectorAll('[data-xm-countdown]').forEach((root) => {
      const endIso = root.dataset.xmCountdownEnd;
      if (!endIso) return;
      const end = new Date(endIso).getTime();
      if (isNaN(end)) return;
      const dEl = root.querySelector('[data-cd="d"]');
      const hEl = root.querySelector('[data-cd="h"]');
      const mEl = root.querySelector('[data-cd="m"]');
      const sEl = root.querySelector('[data-cd="s"]');
      let lastSec = -1;
      const tick = () => {
        const ms = Math.max(0, end - Date.now());
        const days = Math.floor(ms / 86400000);
        const hours = Math.floor((ms / 3600000) % 24);
        const mins = Math.floor((ms / 60000) % 60);
        const secs = Math.floor((ms / 1000) % 60);
        if (dEl) dEl.textContent = String(days).padStart(2, '0');
        if (hEl) hEl.textContent = String(hours).padStart(2, '0');
        if (mEl) mEl.textContent = String(mins).padStart(2, '0');
        if (sEl) {
          sEl.textContent = String(secs).padStart(2, '0');
          if (secs !== lastSec) {
            sEl.animate(
              [
                { transform: 'scale(1.18)', opacity: 0.55 },
                { transform: 'scale(1)', opacity: 1 },
              ],
              { duration: 380, easing: 'cubic-bezier(.2,0,0,1)' }
            );
            lastSec = secs;
          }
        }
        if (ms < 86400000) root.closest('.xm-cd')?.classList.add('xm-cd--ending');
      };
      tick();
      setInterval(tick, 1000);
    });

    /* ---------- 6. Count-up numérico (chips, badges) ---------- */
    const parseNumeric = (txt) => {
      const m = (txt || '').match(/[\d.,]+/);
      if (!m) return null;
      return parseFloat(m[0].replace(/\./g, '').replace(',', '.'));
    };
    const formatNumeric = (n, original) => {
      const useDot = original && original.includes('.');
      const intStr = Math.round(n).toString();
      if (useDot) return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return intStr;
    };
    const countUpObserver = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const original = el.textContent;
          const target = parseNumeric(original);
          if (target == null) {
            obs.unobserve(el);
            return;
          }
          const prefix = (original.match(/^[^\d]*/) || [''])[0];
          const suffix = (original.match(/[^\d.,]*$/) || [''])[0];
          const dur = parseInt(el.dataset.xmCountupDuration || '1400', 10);
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 3);
            el.textContent = prefix + formatNumeric(target * eased, original) + suffix;
            if (t < 1) requestAnimationFrame(tick);
            else el.textContent = original;
          };
          requestAnimationFrame(tick);
          obs.unobserve(el);
        });
      },
      { threshold: 0.4 }
    );
    document.querySelectorAll('[data-xm-countup]').forEach((el) => countUpObserver.observe(el));

    /* ---------- GSAP-only features (parallax, headline mask, hero scale) ---------- */
    const startGsap = () => {
      if (!window.gsap) return;
      const gsap = window.gsap;
      if (window.ScrollTrigger) gsap.registerPlugin(window.ScrollTrigger);

      /* Parallax */
      if (window.ScrollTrigger) {
        document.querySelectorAll('[data-xm-parallax]').forEach((el) => {
          const speed = parseFloat(el.dataset.xmParallax || '0.2');
          gsap.to(el, {
            yPercent: -10 * speed,
            ease: 'none',
            scrollTrigger: {
              trigger: el.closest('[data-xm-parallax-scope]') || el.parentElement || el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          });
        });
      }

      /* Headline mask reveal — palabra por palabra desde abajo */
      document.querySelectorAll('[data-xm-headline-mask]').forEach((el) => {
        if (!el.querySelector('.xm-mask-line')) {
          const html = el.innerHTML;
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const wrapWords = (node) => {
            if (node.nodeType === 3) {
              const frag = document.createDocumentFragment();
              node.textContent.split(/(\s+)/).forEach((part) => {
                if (/\s+/.test(part)) frag.appendChild(document.createTextNode(part));
                else if (part) {
                  const w = document.createElement('span');
                  w.className = 'xm-mask-line';
                  const inner = document.createElement('span');
                  inner.textContent = part;
                  w.appendChild(inner);
                  frag.appendChild(w);
                }
              });
              node.replaceWith(frag);
            } else if (node.childNodes && node.childNodes.length) {
              [...node.childNodes].forEach(wrapWords);
            }
          };
          [...tmp.childNodes].forEach(wrapWords);
          el.innerHTML = tmp.innerHTML;
        }
        const inners = el.querySelectorAll('.xm-mask-line > span');
        gsap.fromTo(
          inners,
          { yPercent: 110 },
          {
            yPercent: 0,
            duration: 1.0,
            ease: 'power4.out',
            stagger: 0.06,
            delay: parseFloat(el.dataset.xmDelay || '0.15'),
          }
        );
      });

      /* Hero floating chips */
      document.querySelectorAll('[data-xm-float]').forEach((el, i) => {
        gsap.to(el, {
          y: '+=10',
          duration: 2.6 + i * 0.35,
          ease: 'sine.inOut',
          yoyo: true,
          repeat: -1,
        });
      });

      /* Hero media: scale + parallax al scroll */
      if (window.ScrollTrigger) {
        document.querySelectorAll('[data-xm-hero-media]').forEach((el) => {
          gsap.to(el, {
            yPercent: -10,
            scale: 0.96,
            ease: 'none',
            scrollTrigger: {
              trigger: el.closest('.xm-hc') || el,
              start: 'top top',
              end: 'bottom top',
              scrub: 0.6,
            },
          });
        });

        /* Marquee con scroll-velocity: JS toma control del track. */
        document.querySelectorAll('[data-xm-marquee-track]').forEach((track) => {
          track.style.animation = 'none';
          const baseSpeed = parseFloat(track.dataset.xmMarqueeSpeed || '50'); // px/s
          let xPos = 0;
          let lastTime = performance.now();
          let velocityBoost = 0;
          let lastScrollY = window.scrollY;
          let paused = false;
          track.addEventListener('mouseenter', () => (paused = true));
          track.addEventListener('mouseleave', () => (paused = false));
          window.addEventListener(
            'scroll',
            () => {
              const dy = window.scrollY - lastScrollY;
              velocityBoost = Math.min(8, Math.abs(dy) * 0.4);
              lastScrollY = window.scrollY;
            },
            { passive: true }
          );
          const halfW = track.scrollWidth / 2 || 1;
          const animate = (now) => {
            const dt = Math.min(50, now - lastTime) / 1000;
            lastTime = now;
            if (!paused) {
              const px = (baseSpeed + velocityBoost * 60) * dt;
              xPos -= px;
              if (xPos < -halfW) xPos += halfW;
            }
            track.style.transform = `translate3d(${xPos}px, 0, 0)`;
            velocityBoost *= 0.93;
            requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        });
      }

      /* Cards lift sutil al entrar al viewport (scrollTrigger.batch) */
      if (window.ScrollTrigger && window.ScrollTrigger.batch) {
        const cards = document.querySelectorAll(
          '.product-card, .grid-product__inner, .featured-collection-item, [data-xm-card]'
        );
        if (cards.length) {
          gsap.set(cards, { opacity: 0, y: 28 });
          window.ScrollTrigger.batch(cards, {
            start: 'top 92%',
            onEnter: (els) =>
              gsap.to(els, {
                opacity: 1,
                y: 0,
                duration: 0.7,
                ease: 'power3.out',
                stagger: 0.06,
                overwrite: true,
              }),
            once: true,
          });
        }
      }
    };

    if (window.gsap) startGsap();
    else
      window.addEventListener('load', () => {
        if (window.gsap) startGsap();
      });
  });
})();
